// MACHINERY V2 (Stroje) — the fleet SCROLL AREA: notices, strip, view branch
// (split out of FleetPage.tsx at M2.13).
//
// >>> WHY THIS FILE EXISTS. <<< FleetPage was at 290 of the project's 300 lines
// and M2.13 must ADD to it (a third view branch + the table's sort state). The
// ticket's near-cap rule requires the split to be the sub-phase's FIRST task,
// before any new lines land. The seam is the scroll container: everything the
// page composes ABOVE it (rail, toolbar) is chrome the user acts on, everything
// inside it is a rendering of the current result set. FleetPage keeps the hooks,
// the handlers and the shell; this file keeps the branch.
//
// THE ORDER OF THE BRANCH IS LOAD-BEARING AND IS PRESERVED VERBATIM (review F1
// / F4, M2.2):
//   · the "status filters ignored" note renders on its own terms;
//   · the LiveFleetStrip renders ABOVE the loading/empty chain, so toggling a
//     chip that matches nothing cannot unmount the strip holding it;
//   · only then loading -> empty -> view.
// Moving the strip into the chain is the exact bug F1 fixed. Do not reorder.
//
// THE STRIP STAYS V1-ONLY (M2.2 / M2.3 as-built): Fleet V2 carries the same
// information per section in its group health headers, and M2.13's table does
// the same through the SAME headers (FleetGroupHeader). The status FILTERS stay
// reachable in every view from the FilterRail's "Stav" section — one
// `fleetStatusFilters` state — so switching view never loses a filter.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { MachineryV2FleetView } from "../machineryV2UiSlice";
import { FilterNote, Notice } from "./FleetNotices";
import { FleetScope } from "./FleetToolbar";
import FleetV1Page from "./FleetV1Page";
import FleetV2Page, { FleetV2PageProps } from "./FleetV2Page";
import LiveFleetStrip from "./LiveFleetStrip";
import { MachineryV2CardState } from "./cardStates";
import { FleetPageData } from "./useFleetPageData";
import FleetTableView from "./table/FleetTableView";
import { FleetTableSort, FleetTableSortColumn } from "./table/fleetTableSort";

/** Everything a body needs EXCEPT the sections it renders — one object, built
 *  once by the page, handed to whichever body is mounted. */
export type FleetBodyProps = Omit<FleetV2PageProps, "sections">;

export interface FleetContentProps {
  data: FleetPageData;
  scope: FleetScope;
  view: MachineryV2FleetView;
  statusFilters: MachineryV2CardState[];
  onToggleStatus: (state: MachineryV2CardState) => void;
  /** V3 only — held by the page so a V1/V3 round trip keeps the ordering. */
  sort: FleetTableSort;
  onSortChange: (column: FleetTableSortColumn) => void;
  body: FleetBodyProps;
}

const FleetContent: React.FC<FleetContentProps> = ({
  data, scope, view, statusFilters, onToggleStatus, sort, onSortChange, body,
}) => {
  const { translate } = useTranslation("myMachinery");

  return (
    <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "0 24px 24px" }}>
      {/* REVIEW F4 — status filters are telemetry-derived, and the
          decommissioned scope has no telemetry. Rather than silently
          matching nothing, say so. The filter is never cleared behind the
          user's back; switching back to Aktívne restores it. */}
      {data.statusFiltersIgnored && (
        <FilterNote text={translate("myMachinery:v2.fleet.statusFiltersIgnored")} />
      )}

      {/* REVIEW F1 — RENDERED ON ITS OWN TERMS, ABOVE THE BRANCH. Inside
          the loading/empty chain, toggling a status chip that matched
          nothing unmounted the very strip holding it, and any thunk that
          flips `loading` (createMachine / restoreMachine included) flashed
          it away mid-action. A filter control must outlive the result of
          its own filter. */}
      {scope === "active" && view === "V1" && (
        <LiveFleetStrip
          total={data.flat.length}
          connected={data.connectedCount}
          counts={data.stateCounts}
          selected={statusFilters}
          onToggle={onToggleStatus}
        />
      )}

      {data.isLoading ? (
        <Notice text={translate("myMachinery:v2.fleet.loading")} dashed={false} />
      ) : data.visibleCount === 0 ? (
        <Notice
          text={translate(
            scope === "decommissioned"
              ? "myMachinery:noDecommissionedMachines"
              : "myMachinery:noMachinesFound"
          )}
        />
      ) : view === "V3" ? (
        <FleetTableView
          sections={data.sections}
          sort={sort}
          onSortChange={onSortChange}
          {...body}
        />
      ) : view === "V2" ? (
        <FleetV2Page sections={data.sections} {...body} />
      ) : (
        <FleetV1Page sections={data.sections} {...body} />
      )}
    </div>
  );
};

export default FleetContent;
